import type { IconSvgElement } from '@hugeicons/react';
import { HugeIcon } from '../ui/HugeIcon';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarRail,
  useSidebar,
} from '../ui/sidebar';
import { SeatUsageCard } from './SeatUsageCard';
import { LicensePeriodCard } from './LicensePeriodCard';

interface ConsoleSidebarChild<ViewId extends string> {
  count?: number;
  id: ViewId;
  label: string;
  children?: Array<ConsoleSidebarChild<ViewId>>;
}

export interface ConsoleSidebarItem<ViewId extends string> {
  id: ViewId;
  label: string;
  icon: IconSvgElement;
  children?: Array<ConsoleSidebarChild<ViewId>>;
}

interface ConsoleSidebarProps<ViewId extends string> {
  activeView: ViewId;
  label: string;
  navItems: Array<ConsoleSidebarItem<ViewId>>;
  onNavigate: (view: ViewId) => void;
  licensePeriod?: { startDate?: string | null; expiryDate?: string };
  seatUsage?: { used: number; limit: number };
  subtitle?: string;
}

export function ConsoleSidebar<ViewId extends string>({
  activeView,
  label,
  navItems,
  onNavigate,
  licensePeriod,
  seatUsage,
  subtitle,
}: ConsoleSidebarProps<ViewId>) {
  const { isMobile, setOpenMobile, state } = useSidebar();
  const displayLabel = label.replace(/^local\s+/i, '');
  const iconOnly = state === 'collapsed' && !isMobile;

  function navigate(view: ViewId) {
    onNavigate(view);
    if (isMobile) setOpenMobile(false);
  }

  function containsActiveItem(item: ConsoleSidebarChild<ViewId>): boolean {
    return item.id === activeView || Boolean(item.children?.some(containsActiveItem));
  }

  function renderSubItems(items: Array<ConsoleSidebarChild<ViewId>>, depth = 0) {
    return (
      <SidebarMenuSub className={depth ? 'ml-2.5 mr-0 mt-1 pl-2.5 pr-0' : 'ml-3.5 mr-0 mt-1 pl-2.5 pr-0'}>
        {items.map((item) => {
          const itemActive = containsActiveItem(item);
          return (
            <SidebarMenuSubItem key={item.id}>
              <SidebarMenuSubButton
                className={`h-8 w-full rounded-md px-3 text-left text-[13px] text-sidebar-foreground/60 data-active:font-medium ${itemActive && item.id !== activeView ? 'font-medium text-sidebar-foreground/80' : ''}`}
                isActive={item.id === activeView}
                onClick={() => navigate(item.id)}
                render={<button type="button" />}
              >
                <span className="min-w-0 flex-1 truncate text-left">{item.label}</span>
                {item.count !== undefined ? (
                  <span className="ml-auto shrink-0 rounded-full bg-sidebar-accent px-1.5 py-0.5 text-[10px] font-semibold tabular-nums text-sidebar-accent-foreground">
                    {item.count}
                  </span>
                ) : null}
              </SidebarMenuSubButton>
              {item.children?.length ? renderSubItems(item.children, depth + 1) : null}
            </SidebarMenuSubItem>
          );
        })}
      </SidebarMenuSub>
    );
  }

  return (
    <Sidebar className="border-r border-sidebar-border" collapsible="icon" variant="sidebar">
      <SidebarHeader className="h-16 justify-center px-3 py-2 group-data-[collapsible=icon]:items-center group-data-[collapsible=icon]:p-2">
        <div className="flex w-full items-center gap-2.5">
          <span className="size-10 shrink-0 overflow-hidden rounded-[10px] border border-black/30 bg-slate-900 shadow-[inset_0_1px_0_rgba(255,255,255,0.18),0_1px_3px_rgba(15,23,42,0.12)] group-data-[collapsible=icon]:size-8 group-data-[collapsible=icon]:rounded-[8px]">
            <img alt="myPDF" className="size-full invert grayscale" src="/favicon.svg" />
          </span>
          {!iconOnly ? (
            <div className="min-w-0 flex-1">
              <p className="truncate text-[13px] font-semibold leading-5 text-sidebar-foreground">{displayLabel}</p>
              {subtitle || seatUsage ? (
                <p className="truncate text-[11px] leading-4 text-muted-foreground">
                  {subtitle ?? `${seatUsage?.used ?? 0} of ${seatUsage?.limit ?? 0} licensed seats`}
                </p>
              ) : null}
            </div>
          ) : null}
        </div>
      </SidebarHeader>

      <SidebarContent>
        <SidebarGroup className="px-2.5 py-4">
          {!iconOnly ? <SidebarGroupLabel className="mb-1 h-7 px-2.5 text-[10px] font-medium uppercase tracking-[0.08em] text-muted-foreground/75">Workspace</SidebarGroupLabel> : null}
          <SidebarGroupContent>
            <SidebarMenu className="gap-1">
              {navItems.map((item) => {
                const parentActive = activeView === item.id || item.children?.some(containsActiveItem);
                return (
                <SidebarMenuItem key={item.id}>
                  <SidebarMenuButton
                    className="h-9 rounded-md px-2.5 text-[14px] font-normal text-sidebar-foreground/65 data-active:font-medium data-active:text-sidebar-accent-foreground group-data-[collapsible=icon]:mx-auto group-data-[collapsible=icon]:size-8! group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:rounded-[8px]! group-data-[collapsible=icon]:p-0! group-data-[collapsible=icon]:pl-0! group-data-[collapsible=icon]:pr-0! group-data-[collapsible=icon]:shadow-none group-data-[collapsible=icon]:ring-0"
                    isActive={iconOnly ? Boolean(parentActive) : activeView === item.id}
                    onClick={() => navigate(item.id)}
                    tooltip={item.label}
                  >
                    <HugeIcon icon={item.icon} size={17} strokeWidth={1.8} />
                    {!iconOnly ? <span>{item.label}</span> : <span className="sr-only">{item.label}</span>}
                  </SidebarMenuButton>
                  {item.children?.length && !iconOnly ? (
                    renderSubItems(item.children)
                  ) : null}
                </SidebarMenuItem>
              );})}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter className="px-2.5 pb-3 pt-2 group-data-[collapsible=icon]:p-2">
        {(licensePeriod || seatUsage) && !iconOnly ? (
          <div className="mt-1 space-y-2 group-data-[collapsible=icon]:hidden">
            {licensePeriod ? <LicensePeriodCard expiryDate={licensePeriod.expiryDate} startDate={licensePeriod.startDate} /> : null}
            {seatUsage ? (
              <SeatUsageCard compact limit={seatUsage.limit} used={seatUsage.used} />
            ) : null}
          </div>
        ) : null}
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
}
